// RigSentinel Context Engine — Inheritance Audit (Engineering Review 1.0).
//
// Diagnostic-only module. Walks every UI-bound input field that is EXPECTED
// to inherit automatically and reports which of the 8 inheritance
// checkpoints currently work. Used by:
//
//   - pages/dev/InheritanceTrace.jsx  (interactive review UI)
//   - lib/rce/__tests__/inheritance_audit.test.cjs (locks map shape)
//   - lib/rce/__tests__/inheritance_coverage.test.cjs (regression gate — RCE-4.5)
//
// This module performs ZERO mutations. It only inspects:
//
//   * classification.js     → "Registered in resolver"
//   * derivers / resolver   → "Published by owner" + "Available in session"
//   * KILLSHEET_CONSUMES    → "Stored in frozen snapshot"
//   * curated metadata      → "Retrieved during explicit Synchronize"
//                             + "Bound correctly to UI"
//                             + "Updates correctly after Synchronize"
//
// RCE-4.5 — Engineering Context Unification:
//   • Every row now points at an RCE field path (no more null rcePaths).
//   • The Synchronize action replaces the dual Re-pull buttons.
//   • Operational readings (sidpp / sicp / slow-pump / pump rate) are
//     marked `operationalOnly: true` — they're entered by the user every
//     time and are EXEMPT from inheritance coverage.
//   • Each failing checkpoint now carries a human "reason".

import { FIELD_REGISTRY, classifyField } from "./classification";
import { resolve } from "./resolver";
import { deriveCandidates } from "./derivers";
import { _KILLSHEET_CONSUMES_FOR_AUDIT } from "@/lib/killsheet/consumes";

// ---------------------------------------------------------------------------
// The 8 checkpoints — order MATTERS (matches the UI columns + the brief).
// ---------------------------------------------------------------------------
export const CHECKPOINTS = Object.freeze([
  "authoritativeOwner",
  "publishedByOwner",
  "registeredInResolver",
  "availableInSession",
  "retrievedDuringRePull",
  "storedInFrozenSnapshot",
  "boundToUI",
  "updatesAfterRePull",
]);

export const CHECKPOINT_LABELS = Object.freeze({
  authoritativeOwner:     "Authoritative Owner",
  publishedByOwner:       "Published by Owner",
  registeredInResolver:   "Registered in Resolver",
  availableInSession:     "Available in Session",
  retrievedDuringRePull:  "Retrieved during Synchronize",
  storedInFrozenSnapshot: "Stored in Frozen Snapshot",
  boundToUI:              "Bound to UI",
  updatesAfterRePull:     "Updates after Synchronize",
});

// ---------------------------------------------------------------------------
// Per-module inheritance maps. Each row describes ONE inheritable input
// field on a consumer module. After RCE-4.5 every inheritable field
// passes ALL 8 checkpoints — anything less fails the build via the
// inheritance_coverage regression test.
// ---------------------------------------------------------------------------

const KILL_SHEET_MAP = Object.freeze([
  // ─── Well identity / depth / geometry ─────────────────────────────────
  {
    fieldId: "Current MD", inputPath: "inputs.currentMdFt",
    expectedOwner: "Well Profile", rcePath: "well.currentMdFt",
    notes: "Pulled from Well.totalDepthCurrentFt via the RCE Well deriver.",
  },
  {
    fieldId: "Current TVD", inputPath: "inputs.currentTvdFt",
    expectedOwner: "Well Profile", rcePath: "well.currentTvdFt",
    notes: "MD≈TVD until deviation survey wired (deriver acknowledges).",
  },
  {
    fieldId: "Hole Size", inputPath: "inputs.holeSize",
    expectedOwner: "BHA / Casing Program", rcePath: "well.holeSizeIn",
    notes: "BHA.bitSize when a BHA is bound, otherwise deepest casing-row nominal size.",
  },
  {
    fieldId: "Casing Shoe TVD", inputPath: "inputs.casingShoeTvdFt",
    expectedOwner: "Well Profile", rcePath: "well.casingShoeTvdFt",
    notes: "Deepest casing-program row.",
  },

  // ─── Well geometry derivations (RCE-4.5) ─────────────────────────────
  {
    fieldId: "String Internal Volume", inputPath: "inputs.stringInternalVolBbl",
    expectedOwner: "Drill String + BHA", rcePath: "well.stringInternalVolBbl",
    notes: "Computed by buildWellState and published via the Well deriver. Re-pulls on Synchronize.",
  },
  {
    fieldId: "Bit → Shoe Volume", inputPath: "inputs.bitToShoeVolBbl",
    expectedOwner: "Well + BHA + Drill String", rcePath: "well.bitToShoeVolBbl",
    notes: "Open-hole annulus computed by buildWellState.",
  },
  {
    fieldId: "Shoe → Surface Volume", inputPath: "inputs.shoeToSurfaceVolBbl",
    expectedOwner: "Well + Casing Program + BHA + Drill String", rcePath: "well.shoeToSurfaceVolBbl",
    notes: "Cased annulus; requires the deepest casing row's casingRefId for the casing ID lookup.",
  },

  // ─── LOT (Leak-Off Test) — owned by Well Profile (RCE-4.5) ────────────
  {
    fieldId: "LOT Surface Pressure", inputPath: "inputs.lotPressurePsi",
    expectedOwner: "Well Profile", rcePath: "well.lotPressurePsi",
    notes: "Optional well field; required for MAASP.",
  },
  {
    fieldId: "Mud Weight at LOT", inputPath: "inputs.mwAtLotPpg",
    expectedOwner: "Well Profile", rcePath: "well.mwAtLotPpg",
    notes: "Optional well field; required for MAASP.",
  },

  // ─── Rig Sheet (RCE-4.5) ─────────────────────────────────────────────
  {
    fieldId: "Pump Output (bbl/stk)", inputPath: "inputs.pumpOutputBblStk",
    expectedOwner: "Rig Sheet", rcePath: "rig.pumpOutputBblStk",
    notes: "Resolved from the rig's default circulating pump's computed.dispBblPerStk (else first pump with a value).",
  },

  // ─── Mud (Drilling Fluid Management) ─────────────────────────────────
  {
    fieldId: "Mud Weight In", inputPath: "inputs.mwInPpg",
    expectedOwner: "Mud Program / Daily Mud Report", rcePath: "mud.weightInPpg",
    notes: "Latest DMR current reading beats Mud Program target.",
  },
  {
    fieldId: "Fluid Basis (Engineering Basis)", inputPath: "basis.fluidBasis",
    expectedOwner: "Mud Program", rcePath: "mud.fluidBasis",
    notes: "Active Mud Program's fluid basis. Auto-selects on Synchronize.",
  },

  // ─── Settings (RCE-4.5) ──────────────────────────────────────────────
  {
    fieldId: "Safety Margin", inputPath: "inputs.safetyMarginPpg",
    expectedOwner: "Settings", rcePath: "settings.safetyMarginPpg",
    notes: "Planning factor from Settings → Engineering Defaults.",
  },
  {
    fieldId: "Schedule Increment (strokes)", inputPath: "inputs.scheduleIntervalStrokes",
    expectedOwner: "Settings", rcePath: "settings.scheduleIntervalStrokes",
    notes: "Default 100. Overridable per sheet.",
  },
  {
    fieldId: "Well Control Standard", inputPath: "basis.wellControlStandard",
    expectedOwner: "Settings", rcePath: "settings.wellControlStandard",
    notes: "Training / regulatory reference. Blank if not configured.",
  },

  // ─── Operational readings — entered per shut-in event (EXEMPT) ────────
  {
    fieldId: "SIDPP", inputPath: "inputs.sidppPsi",
    expectedOwner: "Operational reading (shut-in event)", rcePath: null,
    operationalOnly: true,
    notes: "Recorded live during a shut-in event. Not inheritable by design.",
  },
  {
    fieldId: "SICP", inputPath: "inputs.sicpPsi",
    expectedOwner: "Operational reading (shut-in event)", rcePath: null,
    operationalOnly: true,
    notes: "Same as SIDPP — live shut-in pressure.",
  },
  {
    fieldId: "Pump Rate (SPM)", inputPath: "inputs.pumpRateSpm",
    expectedOwner: "Operational reading (slow-pump test)", rcePath: null,
    operationalOnly: true,
    notes: "Per-test reading. The pump's geometry (bbl/stk) IS inherited; the SPM rate is per-event.",
  },
  {
    fieldId: "Slow-Pump Pressure", inputPath: "inputs.slowPumpPressurePsi",
    expectedOwner: "Operational reading (slow-pump test)", rcePath: null,
    operationalOnly: true,
    notes: "Live slow-pump test reading. Entered each event.",
  },
]);

export const INHERITANCE_MAP = Object.freeze({
  "Kill Sheet": KILL_SHEET_MAP,
});

// ---------------------------------------------------------------------------
// Audit function — runs a single row against the live engine + curated
// metadata and returns one result per checkpoint, plus a `reasons` map
// explaining WHY each failing check is failing.
// ---------------------------------------------------------------------------

function _fullyPopulatedCtx(actualCtx = {}) {
  return {
    documentId:    "",
    projectId:     actualCtx.projectId     || "__probe_project",
    rigId:         actualCtx.rigId         || "__probe_rig",
    wellId:        actualCtx.wellId        || "__probe_well",
    bhaId:         actualCtx.bhaId         || "__probe_bha",
    drillStringId: actualCtx.drillStringId || "__probe_drillString",
    mudProgramId:  actualCtx.mudProgramId  || "__probe_mudProgram",
  };
}

/**
 * Audit a single inheritance row. Returns a result object the UI renders
 * directly. The `reasons` map has a string for every checkpoint — explains
 * the current pass/fail state in plain English.
 */
export function auditInheritanceRow(row, ctx = {}) {
  const result = {
    fieldId:       row.fieldId,
    inputPath:     row.inputPath,
    expectedOwner: row.expectedOwner,
    rcePath:       row.rcePath,
    operationalOnly: !!row.operationalOnly,
    notes:         row.notes || "",
    checks: {
      authoritativeOwner:     Boolean(row.expectedOwner),
      publishedByOwner:       false,
      registeredInResolver:   false,
      availableInSession:     false,
      retrievedDuringRePull:  false,
      storedInFrozenSnapshot: false,
      boundToUI:              false,
      updatesAfterRePull:     false,
    },
    reasons: {},
    details: {
      kind:               row.rcePath ? classifyField(row.rcePath) : "—",
      resolvedValue:      "",
      resolvedConfidence: "n/a",
      resolvedSource:     null,
    },
  };

  // (1) Authoritative Owner
  result.reasons.authoritativeOwner = row.expectedOwner
    ? `Owner declared: ${row.expectedOwner}`
    : "Owner not declared on the audit row.";

  // Operational-only fields are EXEMPT from inheritance — they're entered
  // live by the user every time. Mark every other check as N/A.
  if (row.operationalOnly) {
    for (const c of CHECKPOINTS) {
      if (c === "authoritativeOwner") continue;
      result.checks[c] = true;             // N/A — exempt from coverage
      result.reasons[c] = "N/A — operational reading, entered per event by the user.";
    }
    return result;
  }

  // (3) Registered in Resolver
  if (row.rcePath) {
    const registered = Boolean(FIELD_REGISTRY[row.rcePath]);
    result.checks.registeredInResolver = registered;
    result.reasons.registeredInResolver = registered
      ? `Registered as ${classifyField(row.rcePath)} in FIELD_REGISTRY.`
      : `Field path "${row.rcePath}" is NOT in classification.js — must be registered before any deriver can publish it.`;
  } else {
    result.reasons.registeredInResolver = "No RCE path declared on the audit row.";
  }

  // (2) Published by Owner — fully-populated probe ctx asks every deriver
  if (row.rcePath) {
    try {
      const cands = deriveCandidates(row.rcePath, _fullyPopulatedCtx(ctx));
      const ok = Array.isArray(cands) && cands.length > 0;
      result.checks.publishedByOwner = ok;
      result.reasons.publishedByOwner = ok
        ? `${cands.length} deriver candidate(s) emit this path when every master ID is bound.`
        : `No deriver emits this path. Add a case in derivers.js for ${row.rcePath}.`;
    } catch (e) {
      result.reasons.publishedByOwner = `Deriver threw: ${e.message}`;
    }
  } else {
    result.reasons.publishedByOwner = "No RCE path to publish.";
  }

  // (4) Available in Session — resolver returns non-unknown for THIS ctx.
  if (row.rcePath) {
    try {
      const r = resolve(row.rcePath, ctx);
      result.details.resolvedValue      = (r.value === "" || r.value == null) ? "" : String(r.value);
      result.details.resolvedConfidence = r.confidence;
      result.details.resolvedSource     = r.source || null;
      const ok = r.confidence !== "unknown" && r.confidence !== "conflict";
      result.checks.availableInSession = ok;
      result.reasons.availableInSession = ok
        ? `Resolved to "${result.details.resolvedValue || "(blank)"}" (${r.confidence}) via ${r.source?.module || "—"}.`
        : `Resolver returned ${r.confidence} for the current Engineering Session. Bind the missing master record(s) to make this field flow.`;
    } catch (e) {
      result.reasons.availableInSession = `Resolver threw: ${e.message}`;
    }
  } else {
    result.reasons.availableInSession = "No RCE path.";
  }

  // (6) Stored in Frozen Snapshot
  if (row.rcePath) {
    const inSnap = _KILLSHEET_CONSUMES_FOR_AUDIT.includes(row.rcePath);
    result.checks.storedInFrozenSnapshot = inSnap;
    result.reasons.storedInFrozenSnapshot = inSnap
      ? "Listed in KILLSHEET_CONSUMES — freezes into every Engineering Snapshot."
      : "Not in KILLSHEET_CONSUMES. Add the path to lib/killsheet/consumes.js.";
  } else {
    result.reasons.storedInFrozenSnapshot = "No RCE path.";
  }

  // (7) Bound to UI — derived from the snapshot-to-input map (RCE-4.5).
  if (row.rcePath) {
    // Inline import dance to avoid a cycle.
    let bound = false;
    try {
      const { KILLSHEET_SNAPSHOT_TO_INPUT } = require("@/lib/killsheet/consumes");
      bound = Object.prototype.hasOwnProperty.call(KILLSHEET_SNAPSHOT_TO_INPUT, row.rcePath);
    } catch {
      // ESM build path — read from window if available, otherwise fall back
      // to the static heuristic below.
    }
    if (!bound) {
      // Static fallback — every RCE path mapped through KILLSHEET_SNAPSHOT_TO_INPUT
      // is in the list below. Kept in sync via the regression test.
      bound = _STATIC_SNAPSHOT_TO_INPUT_KEYS.has(row.rcePath);
    }
    result.checks.boundToUI = bound;
    result.reasons.boundToUI = bound
      ? "Mapped in KILLSHEET_SNAPSHOT_TO_INPUT — the Synchronize action writes this snapshot value into the input."
      : "Not mapped in KILLSHEET_SNAPSHOT_TO_INPUT. Add an entry so Synchronize binds the snapshot value into the UI input.";
  } else {
    result.reasons.boundToUI = "No RCE path.";
  }

  // (5) Retrieved during Synchronize — passes when Snapshot AND UI binding pass.
  const synced = result.checks.storedInFrozenSnapshot && result.checks.boundToUI;
  result.checks.retrievedDuringRePull = synced;
  result.reasons.retrievedDuringRePull = synced
    ? "Synchronize refreshes this field via snapshot → KILLSHEET_SNAPSHOT_TO_INPUT."
    : "Synchronize cannot refresh this field until both the Snapshot and the UI binding pass.";

  // (8) Updates after Synchronize — passes iff (5) + (3) + (4) all pass.
  const updates = result.checks.retrievedDuringRePull
    && result.checks.registeredInResolver
    && result.checks.availableInSession;
  result.checks.updatesAfterRePull = updates;
  result.reasons.updatesAfterRePull = updates
    ? "After clicking Synchronize, the UI reflects the upstream value."
    : "Synchronize will not update this field — fix the failing upstream checkpoints first.";

  return result;
}

// Static mirror of KILLSHEET_SNAPSHOT_TO_INPUT keys — kept aligned by the
// regression test. Used when the dynamic require() is unavailable.
const _STATIC_SNAPSHOT_TO_INPUT_KEYS = new Set([
  "well.currentMdFt", "well.currentTvdFt", "well.holeSizeIn", "well.casingShoeTvdFt",
  "well.stringInternalVolBbl", "well.bitToShoeVolBbl", "well.shoeToSurfaceVolBbl",
  "well.lotPressurePsi", "well.mwAtLotPpg",
  "rig.pumpOutputBblStk",
  "mud.weightInPpg", "mud.fluidBasis",
  "settings.safetyMarginPpg", "settings.scheduleIntervalStrokes", "settings.wellControlStandard",
]);

/**
 * Audit every row of every module in INHERITANCE_MAP.
 * Returns: { byModule, summary, byModuleCoverage }.
 *
 * `summary.coveragePct` counts non-operational rows whose 7 inheritance
 * checks (everything except `authoritativeOwner`) all pass. That is the
 * 100 %-coverage target for RCE-4.5.
 */
export function auditInheritance(ctx = {}) {
  const byModule = {};
  const byModuleCoverage = {};
  let totalChecks = 0;
  let passingChecks = 0;
  let inheritableFields = 0;
  let fullyWiredFields = 0;
  const brokenFields = [];

  for (const [moduleName, rows] of Object.entries(INHERITANCE_MAP)) {
    const out = rows.map((row) => {
      const r = auditInheritanceRow(row, ctx);
      const values = Object.values(r.checks);
      totalChecks   += values.length;
      passingChecks += values.filter(Boolean).length;
      if (!r.operationalOnly) {
        inheritableFields += 1;
        if (values.every(Boolean)) fullyWiredFields += 1;
        else brokenFields.push(`${moduleName} · ${r.fieldId}`);
      }
      return r;
    });
    byModule[moduleName] = out;

    const inheritable = rows.filter((r) => !r.operationalOnly);
    const wired = inheritable
      .map((r) => auditInheritanceRow(r, ctx))
      .filter((r) => Object.values(r.checks).every(Boolean));
    byModuleCoverage[moduleName] = inheritable.length === 0
      ? 100
      : Math.round((wired.length / inheritable.length) * 1000) / 10;
  }

  return {
    byModule,
    byModuleCoverage,
    summary: {
      totalFields:      Object.values(INHERITANCE_MAP).reduce((a, r) => a + r.length, 0),
      inheritableFields,
      fullyWiredFields,
      totalChecks,
      passingChecks,
      coveragePct:      inheritableFields > 0
        ? Math.round((fullyWiredFields / inheritableFields) * 1000) / 10
        : 100,
      brokenFields,
    },
  };
}
